import React, { useEffect, useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps';
import { X, ZoomIn, ZoomOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getCentroid } from '@/utils/countryCentroids';
import {
  countriesLoadedFromSummary,
  countriesEntitledFromSummary,
  countriesCoveredEmptyFromSummary,
  countriesNotCoveredFromSummary,
  coverageStateForCountry,
  coverageCells,
  resolvePlayersDefaultCategory,
  visiblePlayerCategories,
  entitlementCountries,
  formatCurrency,
  formatCompactMoney,
  resolveOpportunityType,
  mixSliceColor,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import { fetchMarketSummary, fetchMarketComposition, fetchMarketPlayers } from '@/lib/marketExperienceApi';

function PanelStat({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A]">{label}</p>
      <p className="text-sm font-semibold text-[#0A1A2F] mt-0.5 tabular-nums">{value ?? '—'}</p>
    </div>
  );
}

function PanelSection({ title, children }) {
  if (!children) return null;
  return (
    <div className="pt-2 border-t border-gray-200 first:border-0 first:pt-0">
      <p className="text-[10px] uppercase tracking-wide text-[#5C6B7A] mb-1.5">{title}</p>
      {children}
    </div>
  );
}

function CountList({ items }) {
  if (!items.length) return null;
  return (
    <ul className="space-y-1">
      {items.map((item) => (
        <li key={item.key} className="flex items-baseline justify-between gap-3 text-xs">
          <span className="text-[#0A1A2F] min-w-0 truncate">{item.label}</span>
          <span className="text-[#5C6B7A] tabular-nums shrink-0">{item.count}</span>
        </li>
      ))}
    </ul>
  );
}

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

const MAP_MIN_ZOOM = 0.5;
const MAP_MAX_ZOOM = 18;

// One page is 50 rows, so 40 dropped real institutions from the map for no
// reason once positions became meaningful. Sized to the page, with headroom.
const MARKER_CAP = 120;

/** Smaller countries need a tighter zoom to fill the frame on select. */
const COUNTRY_SELECT_ZOOM = {
  'United Kingdom': 11,
  Ireland: 12,
  Netherlands: 12,
  Belgium: 13,
  Singapore: 16,
  'Hong Kong': 16,
  Denmark: 11,
  Switzerland: 12,
  Austria: 10,
  Portugal: 9,
  'New Zealand': 7,
  France: 6,
  Germany: 6,
  Spain: 6,
  Italy: 6,
  Australia: 4.5,
  Canada: 3.5,
  USA: 3.5,
  'United States': 3.5,
  'South Africa': 5.5,
};

function selectZoomForCountry(label) {
  if (!label) return 6;
  const exact = COUNTRY_SELECT_ZOOM[label];
  if (exact) return exact;
  const hit = Object.entries(COUNTRY_SELECT_ZOOM).find(([name]) => namesMatch(name, label));
  return hit ? hit[1] : 8;
}

const COUNTRY_ALIASES = {
  'United Kingdom': ['united kingdom', 'uk', 'great britain', 'england', 'gb'],
  'United States': ['united states', 'usa', 'us', 'united states of america'],
  'Australia': ['australia'],
  'Canada': ['canada'],
  'France': ['france'],
  'Germany': ['germany'],
  'Netherlands': ['netherlands', 'the netherlands'],
  'Ireland': ['ireland', 'republic of ireland'],
  'South Africa': ['south africa'],
};

function norm(s) {
  return String(s || '').trim().toLowerCase();
}

function namesMatch(a, b) {
  const na = norm(a);
  const nb = norm(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  for (const [canonical, aliases] of Object.entries(COUNTRY_ALIASES)) {
    const set = new Set([canonical.toLowerCase(), ...aliases]);
    if (set.has(na) && set.has(nb)) return true;
  }
  return false;
}

function findEntitledName(geoName, entitled) {
  return entitled.find((c) => namesMatch(c, geoName)) || null;
}

function hashJitter(seed) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ((h % 1000) / 1000) - 0.5;
}

export default function MarketCoverageMap({
  tenantId,
  lens,
  summary,
  preferences,
  opportunityRows = [],
  countryCounts,
  onCountrySelect,
  onAccountClick,
}) {
  const { t } = useTranslation();
  const [tip, setTip] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState([0, 20]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [panel, setPanel] = useState(null);
  const [panelLoading, setPanelLoading] = useState(false);

  const loaded = useMemo(() => countriesLoadedFromSummary(summary), [summary]);
  const entitled = useMemo(() => {
    const fromCoverage = countriesEntitledFromSummary(summary);
    const fromPrefs = entitlementCountries(preferences);
    return [...new Set([...fromCoverage, ...fromPrefs, ...loaded])];
  }, [preferences, summary, loaded]);

  const counts = useMemo(() => {
    // COUNT THE QUEUE, NOT THE PAGE. `opportunityRows` is one page — tallying it
    // made the map read "United Kingdom 9 / United States 41" (= 50, the page
    // size) under a tab labelled 68. The API sends per-country counts over the
    // whole resolved queue; prefer them.
    if (Array.isArray(countryCounts) && countryCounts.length) {
      return Object.fromEntries(
        countryCounts
          .filter((c) => c && c.country)
          .map((c) => [String(c.country).trim(), Number(c.count) || 0]),
      );
    }
    // Fallback for any caller that has not been given countryCounts yet. Wrong
    // when the result is paginated, but never worse than today.
    const map = {};
    (opportunityRows || []).forEach((row) => {
      const c = (row.country || '').trim();
      if (!c) return;
      map[c] = (map[c] || 0) + 1;
    });
    return map;
  }, [countryCounts, opportunityRows]);

  const markers = useMemo(() => {
    const byCountry = {};
    (opportunityRows || []).forEach((row) => {
      const country = (row.country || '').trim();
      if (!country) return;
      if (!byCountry[country]) byCountry[country] = [];
      byCountry[country].push(row);
    });
    const out = [];
    Object.entries(byCountry).forEach(([country, rows]) => {
      const base = getCentroid(country);
      rows.slice(0, MARKER_CAP).forEach((row, i) => {
        // REAL COORDINATES WHEN WE HAVE THEM. The API sends latitude/longitude
        // derived from primary_location for 94-100% of accounts. Everything used
        // to be drawn at the country centroid, which put 1,447 US institutions in
        // one blob over Kansas.
        // Number(null) === 0, and 0 is finite — so coercing first and testing
        // isFinite() second accepts a missing coordinate and pins it at [0, 0], in the
        // Gulf of Guinea. Reject the empty values first, then coerce.
        const num = (v) => (v === null || v === undefined || v === '' ? NaN : Number(v));
        const lat = num(row.latitude);
        const lon = num(row.longitude);

        // Range-check as well as null-check. A coordinate outside these bounds is
        // corrupt rather than merely missing, and the centroid is a better answer than
        // a confidently wrong continent.
        const located =
          Number.isFinite(lat) && Number.isFinite(lon)
          && Math.abs(lat) <= 90 && Math.abs(lon) <= 180
          && !(lat === 0 && lon === 0);

        // Jitter still applies, but at two very different scales. On a real
        // coordinate it exists only to separate institutions that share a city —
        // London has 36 — so it must stay inside that city: +/-0.04 deg is about
        // 3 miles. On the centroid fallback it is the old wide scatter, because
        // there the position carries no meaning anyway and a tight cluster would
        // falsely imply the whole country is in one town.
        const spread = located ? 0.08 : 1.8;
        const spreadY = located ? 0.06 : 1.2;
        const jx = hashJitter(row.account_id || row.account_name || i) * spread;
        const jy = hashJitter(`${row.account_id || ''}-y-${i}`) * spreadY;

        const origin = located ? [lon, lat] : base;
        if (!origin) return;

        out.push({
          id: row.account_id || `${country}-${i}`,
          name: row.account_name,
          coordinates: [origin[0] + jx, origin[1] + jy],
          located,
          color: mixSliceColor(resolveOpportunityType(row)),
          row,
        });
      });
    });
    return out;
  }, [opportunityRows]);

  const emptyLoaded = useMemo(() => countriesCoveredEmptyFromSummary(summary), [summary]);
  const notCovered = useMemo(() => countriesNotCoveredFromSummary(summary), [summary]);

  const stateForName = (name) => {
    const entitledName = findEntitledName(name, entitled);
    if (!entitledName) return { state: 'outside', label: name, count: 0 };
    const apiState = coverageStateForCountry(summary, entitledName);
    if (apiState === 'opportunity') {
      const countKey = Object.keys(counts).find((k) => namesMatch(k, entitledName));
      const n = countKey ? counts[countKey] : 0;
      return { state: 'active', label: entitledName, count: n };
    }
    if (apiState === 'covered_empty' || apiState === 'empty') {
      return { state: 'empty', label: entitledName, count: 0 };
    }
    if (apiState === 'not_covered' || apiState === 'not_yet_covered') {
      return { state: 'uncovered', label: entitledName, count: 0 };
    }
    if (notCovered.some((c) => namesMatch(c, entitledName))) {
      return { state: 'uncovered', label: entitledName, count: 0 };
    }
    const isLoaded = loaded.some((c) => namesMatch(c, entitledName));
    if (!isLoaded) return { state: 'uncovered', label: entitledName, count: 0 };
    const countKey = Object.keys(counts).find((k) => namesMatch(k, entitledName));
    const n = countKey ? counts[countKey] : 0;
    if ((emptyLoaded.some((c) => namesMatch(c, entitledName)) && n === 0) || n === 0) {
      return { state: 'empty', label: entitledName, count: 0 };
    }
    return { state: 'active', label: entitledName, count: n };
  };

  const fillFor = (state) => {
    if (state === 'active') return '#00C2C2';
    if (state === 'uncovered') return '#D6D3D1';
    // empty / outside — no separate “loaded, nothing found” colour
    return '#F3F4F6';
  };

  useEffect(() => {
    if (!selectedCountry || !tenantId || !lens) {
      setPanel(null);
      return;
    }
    let cancelled = false;
    setPanelLoading(true);
    Promise.all([
      // The country panel shows counts only, so it never waits for a commentary to be composed.
      fetchMarketSummary(tenantId, lens, selectedCountry, { brief: 'none' }).catch(() => null),
      fetchMarketComposition(tenantId, lens, selectedCountry).catch(() => null),
      fetchMarketPlayers(tenantId, lens, { country: selectedCountry }).catch(() => null),
    ]).then(([scopedSummary, composition, players]) => {
      if (cancelled) return;
      setPanel({ summary: scopedSummary, composition, players });
    }).finally(() => {
      if (!cancelled) setPanelLoading(false);
    });
    return () => { cancelled = true; };
  }, [selectedCountry, tenantId, lens]);

  const selectCountry = (label) => {
    setSelectedCountry(label);
    onCountrySelect?.(label);
    const c = getCentroid(label);
    if (c) {
      setCenter(c);
      setZoom(selectZoomForCountry(label));
    }
  };

  const clearCountry = () => {
    setSelectedCountry(null);
    setPanel(null);
    setCenter([0, 20]);
    setZoom(1);
  };

  const zoomIn = () => setZoom((z) => Math.min(Number(z) + 1, MAP_MAX_ZOOM));
  const zoomOut = () => setZoom((z) => Math.max(Number(z) - 1, MAP_MIN_ZOOM));

  const funnel = panel?.summary?.funnel || {};
  const value = panel?.summary?.value || {};
  const cells = coverageCells(panel?.summary);
  const playerCats = visiblePlayerCategories(panel?.players);
  const defaultPlayerCat = resolvePlayersDefaultCategory(panel?.players, playerCats);
  const featuredPlayers = playerCats.find((c) => (c.category || c.key) === defaultPlayerCat) || playerCats[0];
  const vendors = (featuredPlayers?.vendors || []).slice(0, 4);
  const sectors = (panel?.composition?.structure?.by_sector || []).slice(0, 4).map((s, i) => ({
    key: s.sector || s.label || s.key || `s-${i}`,
    label: s.sector || s.label || s.key || '—',
    count: s.count,
  }));
  const aiLevels = (panel?.composition?.ai_readiness?.levels || []).map((l, i) => ({
    key: l.level || l.label || `ai-${i}`,
    label: l.label || l.level || '—',
    count: l.count,
  }));
  const financialBands = (panel?.composition?.financial_capacity?.bands || []).map((b, i) => ({
    key: b.band || b.label || `f-${i}`,
    label: b.band || b.label || '—',
    count: b.count,
  }));
  const tam = formatCompactMoney(value.tam, value.currency) || formatCurrency(value.tam, value.currency);
  const sam = formatCompactMoney(value.sam, value.currency) || formatCurrency(value.sam, value.currency);

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-base text-[#0A1A2F]">{t('mktCoverage')}</CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className={`grid gap-4 ${selectedCountry ? 'lg:grid-cols-12' : ''}`}>
          <div className={`relative ${selectedCountry ? 'lg:col-span-8' : ''}`}>
            <div className="absolute bottom-2 right-2 z-10 flex flex-col gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomIn} disabled={zoom >= MAP_MAX_ZOOM}>
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomOut} disabled={zoom <= MAP_MIN_ZOOM}>
                <ZoomOut className="w-4 h-4" />
              </Button>
            </div>
            <ComposableMap projectionConfig={{ scale: 145 }} width={800} height={420} className="w-full h-auto">
              <ZoomableGroup
                center={center}
                zoom={zoom}
                minZoom={MAP_MIN_ZOOM}
                maxZoom={MAP_MAX_ZOOM}
                onMoveEnd={({ coordinates, zoom: z }) => {
                  setCenter(coordinates);
                  setZoom(Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, z)));
                }}
              >
                <Geographies geography={GEO_URL}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const geoName = geo.properties?.name || geo.properties?.NAME;
                      const { state, label, count } = stateForName(geoName);
                      const selected = selectedCountry && namesMatch(selectedCountry, label);
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={selected ? '#0A1A2F' : fillFor(state)}
                          stroke={state === 'uncovered' ? '#78716C' : '#FFFFFF'}
                          strokeWidth={state === 'uncovered' ? 0.8 : 0.4}
                          strokeDasharray={state === 'uncovered' ? '2 2' : undefined}
                          onMouseEnter={(evt) => {
                            if (state === 'outside') return;
                            setTip({
                              x: evt.clientX,
                              y: evt.clientY,
                              text:
                                state === 'uncovered'
                                  ? t('mktMapTipNotLoaded', { label })
                                  : state === 'empty'
                                    ? t('mktMapTipEmpty', { label })
                                    : (count === 1
                                      ? t('mktMapTipOneOpportunity', { label, count })
                                      : t('mktMapTipNOpportunities', { label, count })),
                            });
                          }}
                          onMouseLeave={() => setTip(null)}
                          onClick={() => {
                            if (state === 'outside') return;
                            selectCountry(label);
                          }}
                          style={{
                            default: { outline: 'none', cursor: state === 'outside' ? 'default' : 'pointer' },
                            hover: { outline: 'none', fill: state === 'outside' ? '#F3F4F6' : '#0A1A2F' },
                            pressed: { outline: 'none' },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
                {markers.map((m) => (
                  <Marker key={m.id} coordinates={m.coordinates}>
                    <circle
                      r={3.5 / Math.max(zoom, 1)}
                      fill={m.color}
                      stroke="#fff"
                      strokeWidth={0.8 / Math.max(zoom, 1)}
                      style={{ cursor: onAccountClick ? 'pointer' : 'default' }}
                      onMouseEnter={(evt) => {
                        setTip({ x: evt.clientX, y: evt.clientY, text: m.name || t('account') });
                      }}
                      onMouseLeave={() => setTip(null)}
                      onClick={(evt) => {
                        evt.stopPropagation();
                        onAccountClick?.(m.row);
                      }}
                    />
                  </Marker>
                ))}
              </ZoomableGroup>
            </ComposableMap>
            {tip && (
              <div
                className="pointer-events-none fixed z-50 rounded-md bg-[#0A1A2F] text-white text-xs px-2 py-1"
                style={{ left: tip.x + 12, top: tip.y + 12 }}
              >
                {tip.text}
              </div>
            )}
          </div>

          {selectedCountry && (
            <div className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-4 text-sm">
              <div className="flex items-start justify-between gap-2 mb-3">
                <p className="font-semibold text-[#0A1A2F] text-base leading-tight">{selectedCountry}</p>
                <button type="button" className="text-[#5C6B7A] shrink-0" onClick={clearCountry} aria-label={t('mktCloseCountryPanel')}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              {panelLoading ? (
                <div className="h-40 bg-gray-100 rounded-md animate-pulse" />
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <PanelStat label={t('mktInstitutions')} value={funnel.known ?? funnel.addressable} />
                    <PanelStat label={t('mktInYourMarket')} value={funnel.in_market} />
                    <PanelStat label={t('mktPriority')} value={funnel.priority} />
                  </div>

                  {(tam || sam) && (
                    <PanelSection title={t('mktWhatItIsWorth')}>
                      <div className="grid grid-cols-2 gap-2">
                        <PanelStat label="TAM" value={tam} />
                        <PanelStat label="SAM" value={sam} />
                      </div>
                    </PanelSection>
                  )}

                  {cells && (
                    <PanelSection title={t('mktHowMuchWeHaveLookedAt')}>
                      <p className="text-xs text-[#0A1A2F]">
                        <span className="font-semibold tabular-nums">{cells.examined}</span>
                        {t('mktOfSeparator')}
                        <span className="tabular-nums">{cells.examinable}</span>
                        {t('mktExaminedSuffix')}
                      </p>
                    </PanelSection>
                  )}

                  {vendors.length > 0 && (
                    <PanelSection title={t('mktWhoIsInPlace')}>
                      <ol className="space-y-1">
                        {vendors.map((v, i) => (
                          <li key={v.key || v.label || i} className="flex items-baseline gap-2 text-xs text-[#0A1A2F]">
                            <span className="text-[#5C6B7A] tabular-nums w-3 shrink-0">{i + 1}</span>
                            <span className="truncate">{v.label || v.key}</span>
                          </li>
                        ))}
                      </ol>
                    </PanelSection>
                  )}

                  {sectors.length > 0 && (
                    <PanelSection title={t('sectors')}>
                      <CountList items={sectors} />
                    </PanelSection>
                  )}

                  {aiLevels.length > 0 && (
                    <PanelSection title={t('mktAiReadiness')}>
                      <CountList items={aiLevels} />
                    </PanelSection>
                  )}

                  {financialBands.length > 0 && (
                    <PanelSection title={t('mktFinancialCapacity')}>
                      <CountList items={financialBands} />
                    </PanelSection>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mt-3 text-[11px] text-[#5C6B7A]">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#00C2C2]" /> {t('mktHasOpportunities')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#D6D3D1] border border-dashed border-stone-500" /> {t('mktNotLoadedYet')}
          </span>
          {markers.length > 0 ? (
            <span>{t('mktShowingAccountsOnPage')}</span>
          ) : null}
        </div>
        {entitled.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {entitled.map((country) => {
              const { state, count } = stateForName(country);
              const selected = selectedCountry && namesMatch(selectedCountry, country);
              const status =
                state === 'uncovered'
                  ? t('mktStatusNotLoaded')
                  : state === 'empty'
                    ? t('mktStatusEmpty')
                    : `${count}`;
              const swatch =
                state === 'active'
                  ? 'bg-[#00C2C2]'
                  : state === 'uncovered'
                    ? 'bg-[#D6D3D1] border border-dashed border-stone-500'
                    : 'bg-[#F3F4F6] border border-gray-200';
              return (
                <button
                  key={country}
                  type="button"
                  onClick={() => selectCountry(country)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs border ${
                    selected
                      ? 'border-[#0A1A2F] bg-[#ECFFFE]'
                      : 'border-gray-200 bg-white hover:border-[#00C2C2]'
                  }`}
                >
                  <span className={`inline-block w-2 h-2 rounded-sm shrink-0 ${swatch}`} />
                  <span className="font-medium text-[#0A1A2F]">{country}</span>
                  <span className="text-[#5C6B7A]">{status}</span>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
